import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import {
    APIS,
    SF_CO_CD_INFO,
    CO_CD_INPUT_TRGT_SET,
    ATCH_FILE_VISIBILITY_MAP,
    CO_CD_INPUT_PROPERTIES,
    URLS,
} from "@/tra/tancis/ptli/usr/cocd/services/PtliCoCdService";

import {
    CO_CD_LCNE_SET,
    CO_CD_INPUT_TASAC_SET,
    APIS as CO_APIS,
} from "@/tra/tancis/ptli/usr/co/services/PtliCoService";
import { getNameOfJSDocTypedef } from "typescript";

/*
 * Company Code Registration
 * !== 업체 코드 등록 ==!
 */
export const PTLI0407002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { coTin, coSn } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    const lcmWrhsCoTpMapRef = useRef<any>({});
    const [currLcmWrhs, setCurrLcmWrhs] = useState<any>({});

    const [epzPrmss, setEpzPrmss] = useState<any>({});

    const [hasCFA, setHasCFA] = useState(false);

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "cstmCd" },
            { area: "cntyCd" },
            { area: "coCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Company Code Information !== 업체 코드 정보 ==!
        coCdInfo: useForm({
            defaultSchema: SF_CO_CD_INFO,
        }),
    };

    const grid = {};

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 업체 사용자 정보 조회 ==!
        getCoCdInfo: useFetch({
            api: () => APIS.getCoCdInfo(coTin, coSn),
            onSuccess: (data) => {
                const coCdInfo = data.coCdInfo.content;
                form.coCdInfo.setValues(coCdInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Create Company Code !== 업체 코드 등록 ==!
        createCoCd: useFetch({
            api: (data) => APIS.createCoCd({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.ptli0407001q);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coCdInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // delete Company Code !== 업체 코드 삭제 ==!
        deleteCoCd: useFetch({
            api: () => APIS.deleteCoCd(coTin, coSn),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00060", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onCancel: () => {
                        navigate(URLS.ptli0407001q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coCdInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
            showToast: true,
        }),
        // Get License User Information !== 면허 사용자 정보 조회 ==!
        getLcmWrhsCoTpMap: useFetch({
            api: (coTin: string) => APIS.getLcmWrhsCoTpMap(coTin),
            onSuccess: (data) => {
                const lcmCotpMap = data.lcmWrhsCotpMap.content;
                lcmWrhsCoTpMapRef.current = lcmCotpMap;
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        // Get EPZ License Permission Information !== EPZ 면허 허가 정보 ==!
        getEpzLcnePrmssInfo: useFetch({
            api: (coTin: string) => APIS.getEpzLcnePrmssInfo(coTin),
            onSuccess: (data) => {
                const epzLcnePrmssInfo = data.epzLcnePrmssInfo.content;
                setEpzPrmss(epzLcnePrmssInfo);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
        chkWrhsCertificate: useFetch({
            api: (data: any) => APIS.chkWrhsCertificate(data),
            onSuccess: (data, param: any) => {
                const result = data.resultValue.content;
                data.resultValue.content.success = false;

                const todayStr = new Date().toISOString().slice(0, 10).replaceAll("-", "");

                if (result.tasacCrtfStatNm === "Active" && result.tasacCrtfVldEndDt > todayStr) {
                    if (param[0].coTpCd === "FF") {
                        if (result.tasacLcneTpCd !== "CCD") {
                            data.resultValue.content.errorMessage = "msg.ptl.00826";
                        }
                    } else if (param[0].coTpCd === "SA" || param[0].coTpCd === "SL") {
                        if (result.tasacLcneTpCd !== "SHP") {
                            data.resultValue.content.errorMessage = "msg.ptl.00827";
                        }
                    }
                } else {
                    data.resultValue.content.errorMessage = "msg.ptl.00825";
                }
                return;
            },
            onError: (err) => {
                modal.openModal({ content: err.response?.data?.errorMessage ?? "Unknown error" });
            },
            showToast: true,
        }),

        getCoTpListForTin: useFetch({
            api: (coTin: string) => CO_APIS.getCoInfoMgmt(coTin),
            onSuccess: (data) => {
                const list = data?.coInfoMgmt?.content?.coTpList || [];
                const exists = list.some((it: any) => it.coTpCd === "C" || it.coTpCd === "CS");
                setHasCFA(exists);

                // const cur = form.coCdInfo.getValue("coTpCd");
                // if (exists && (cur === "C" || cur === "CS")) {
                //     console.log("초기화 실행 1번");
                //     modal.openModal({ content: "msg.ptl.00831" });
                //     setTimeout(() => form.coCdInfo.setValue("coTpCd", ""), 0);
                // }
            },
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Save Button !== 저장 버튼 클릭 ==!
        onSaveBtnClick: async () => {
            if (!comnUtils.isEmpty(form.coCdInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            const data = form.coCdInfo.getValues();

            let hasError = false;
            let errorMessage;

            if (CO_CD_INPUT_TASAC_SET.has(data.coTpCd)) {
                data.tasacRgsrNo = form.coCdInfo.getValue("coTin");
                const res = await fetch.chkWrhsCertificate.fetch(data);
                // errorMessage = res.message;
                if (res.resultValue !== undefined) {
                    errorMessage = res.resultValue.content.errorMessage;
                    if (errorMessage !== undefined) {
                        hasError = true;
                    }
                } else {
                    return;
                }
            }

            if (hasError) {
                modal.openModal({
                    content: errorMessage,
                });
                return;
            }
            // return false;
            handler.onSaveCoCd();
        },

        onSaveCoCd: () => {
            form.coCdInfo.handleSubmit(
                () => {
                    const data = form.coCdInfo.getValues();

                    if (typeof data.bsopId === "object") {
                        data.atchFile = data.bsopId;
                        data.bsopId = null;
                    }

                    if (CO_CD_LCNE_SET.has(data.coTpCd) && data.hasOwnProperty("coCdSelect")) {
                        data.coCd = data.coCdSelect;
                    }

                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createCoCd.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },

        // Delete Company Code !== 업체 코드 삭제 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    fetch.deleteCoCd.fetch();
                },
            });
        },
    };

    useEffect(() => {
        //fetch.getCoCdInfo.fetch();
        // fetch.getCoTpAplyList.fetch();
    }, []);

    const coTpCd = form.coCdInfo.watch("coTpCd");
    const inpCoTin = form.coCdInfo.watch("coTin");
    const coCdSelect = form.coCdInfo.watch("coCdSelect");

    useEffect(() => {
        if (!!inpCoTin) {
            fetch.getLcmWrhsCoTpMap.fetch(inpCoTin);
            fetch.getCoTpListForTin.fetch(inpCoTin);
        } else {
            setHasCFA(false);
        }
    }, [inpCoTin]);

    useEffect(() => {
        if (!!inpCoTin && hasCFA) {
            const cur = form.coCdInfo.getValue("coTpCd");
            if (cur === "C" || cur === "CS") {
                modal.openModal({ content: "msg.ptl.00831" });
                setTimeout(() => form.coCdInfo.setValue("coTpCd", ""), 0);
            }
        }
    }, [inpCoTin, hasCFA]);

    useEffect(() => {
        form.coCdInfo.setValue("cstmOfceCd", "");
        form.coCdInfo.setValue("coCd", "");
        form.coCdInfo.setValue("coCdSelect", "");
    }, [coTpCd]);

    const isCoCdInputTrgt = () => {
        return CO_CD_INPUT_TRGT_SET.has(coTpCd);
    };

    const isAtchFileVisible = (field: string) => {
        const visibleFields = ATCH_FILE_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isLcmWrhsSelectTrgt = (coTpCdParam: string = coTpCd) => {
        return CO_CD_LCNE_SET.has(coTpCdParam) && coTpCdParam !== "C" && coTpCdParam !== "CS";
        //return CO_CD_LCNE_SET.has(coTpCdParam) && coTpCdParam !== "C";
    };

    const findLcmsWrhs = (coTpCd: string, coCd: string) => {
        const lcmWrhsCoTpMap = lcmWrhsCoTpMapRef.current;
        if (lcmWrhsCoTpMap?.hasOwnProperty(coTpCd)) {
            const currLcm = lcmWrhsCoTpMap[coTpCd].find((x: any) => x.coCd === coCd);
            return currLcm || null;
        }
        return null;
    };

    const getLcmWrhsOptionArr = () => {
        let lcmWrhsCoTpOptions: any[] = [];
        const lcmWrhsCoTpMap = lcmWrhsCoTpMapRef.current;
        if (isLcmWrhsSelectTrgt() && lcmWrhsCoTpMap?.hasOwnProperty(coTpCd)) {
            lcmWrhsCoTpOptions = lcmWrhsCoTpMap[coTpCd]?.map((item: any) => ({
                label: item.coCd,
                value: item.coCd,
                cstmOfceCd: item.glblCstmOfceCd,
                cstmOfceCdNm: item.glblCstmOfceNm,
            }));
        }
        return lcmWrhsCoTpOptions;
    };

    // const isCstmOfceCdSelectableLcne = () => {
    //     return new Set(["WE", "WG", "WP"]).has(coTpCd); // EPZ LICENSE
    // };
    // coTpCd가 WE, WG, WP일 때, true 값을 출력 또는 glblCstmOfceCd가 null값이거나 빈 값이 들어올 때 true를 출력
    const isCstmOfceCdSelectableLcne = () => {
        const isEpzType = new Set(["WE", "WG", "WP"]).has(coTpCd);
        const isGlblCstmOfceCdNull = comnUtils.isEmpty(currLcmWrhs?.glblCstmOfceCd);
        // 이 조건을 만족할 때만 검색 비활성화 -> 그 외는 모두 검색 가능
        return isEpzType || isGlblCstmOfceCdNull;
    };

    const isLcneUseTpCdSelectTrgt = (coTpCdParam: string = coTpCd) => {
        return "C" === coTpCdParam;
    };

    const onLcmWrhsCstmOfceCdChange = () => {
        if (isCstmOfceCdSelectableLcne()) {
            // EPZ 면허 보유 세관 수를 면허 수로 제한
            const epzLcnePrmssInfo = epzPrmss;
            if (!comnUtils.isUndefined(epzLcnePrmssInfo)) {
                let epzLcneCnt = epzLcnePrmssInfo.epzLcneCnt; // EPZ 면허 수
                let cstmOfceCdCnt = epzLcnePrmssInfo.cstmOfceCdCnt; // EPZ 면허 등록 세관 수
                let cstmOfceCdSet = epzLcnePrmssInfo.cstmOfceCdSet; // EPZ 면허 등록 세관
                if (epzLcneCnt > cstmOfceCdCnt) {
                    // EPZ 면허 수보다 등록된 세관 수가 적은 경우
                    return;
                }
                const cstmOfceCd = form.coCdInfo.getValue("cstmOfceCd");
                if (epzLcneCnt >= 1 && epzLcneCnt == cstmOfceCdCnt) {
                    // 면허 1개 이상 발급 && EPZ 면허 수와 등록된 세관 수가 동일한 경우
                    if (!comnUtils.isUndefined(cstmOfceCd) && !comnUtils.isEmptyString(cstmOfceCd)) {
                        // 기존에 등록된 세관을 포함하는 경우
                        if (cstmOfceCdSet.includes(cstmOfceCd)) {
                            return;
                        }
                    }
                }
            }
            form.coCdInfo.setValue("cstmOfceCd", currLcmWrhs.glblCstmOfceCd);
        } else {
            form.coCdInfo.setValue("cstmOfceCd", currLcmWrhs.glblCstmOfceCd);
        }
    };

    const onLcmWrhsCoCdSlctChange = () => {
        let bfrCoCdSelect = "";
        if (currLcmWrhs?.hasOwnProperty("coCd")) {
            bfrCoCdSelect = currLcmWrhs.coCd;
        }

        const coTpCd = form.coCdInfo.getValue("coTpCd");
        const coCdSelect = form.coCdInfo.getValue("coCdSelect");
        if (isLcmWrhsSelectTrgt()) {
            const currLcm = findLcmsWrhs(coTpCd, coCdSelect);
            if (!!currLcm) {
                if (bfrCoCdSelect !== currLcm?.coCd) {
                    form.coCdInfo.setValue("cstmOfceCd", currLcm.glblCstmOfceCd);
                    form.coCdInfo.setValue("coCd", currLcm?.cocd);
                    setCurrLcmWrhs(currLcm);
                }
            } else {
                form.coCdInfo.setValue("cstmOfceCd", "");
                form.coCdInfo.setValue("coCd", "");
                setCurrLcmWrhs({});
            }
        } else {
            if (Object.keys(currLcmWrhs).length !== 0) {
                form.coCdInfo.setValue("cstmOfceCd", "");
                form.coCdInfo.setValue("coCd", "");
            }
            setCurrLcmWrhs({});
        }
    };

    const isCoCdInputTasacTrgt = () => CO_CD_INPUT_TASAC_SET.has(coTpCd);
    const isSaveCocd = () => {
        return typeof coSn !== "undefined";
    };

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                {/* Company Type */}
                                <Group.Control
                                    {...form.coCdInfo.schema.coTpCd}
                                    onChange={(val: any) => {
                                        const selected = val?.target?.value ?? val;

                                        if ((selected === "C" || selected === "CS") && hasCFA) {
                                            modal.openModal({ content: "msg.ptl.00831" });
                                            setTimeout(() => form.coCdInfo.setValue("coTpCd", ""), 0);
                                            return;
                                        } else {
                                            // 정상 반영
                                            form.coCdInfo.setValue("coTpCd", selected);
                                        }
                                        form.coCdInfo.setValue("coTpCd", selected);
                                    }}
                                />
                                {/* Company office CodeType */}
                                {!isLcmWrhsSelectTrgt() && (
                                    <Group.Control {...form.coCdInfo.schema.cstmOfceCd}></Group.Control>
                                )}
                                {isLcmWrhsSelectTrgt() && (
                                    <Group.Control
                                        {...form.coCdInfo.schema.cstmOfceCd}
                                        disableSearch={!isCstmOfceCdSelectableLcne()}
                                        readOnlyInput={!isCstmOfceCdSelectableLcne()}
                                        borderColor={!isCstmOfceCdSelectableLcne() ? "info" : ""}
                                        onChange={() => onLcmWrhsCstmOfceCdChange()}
                                    />
                                )}
                            </Group.Row>
                            <Group.Row>
                                {!isLcmWrhsSelectTrgt() && isCoCdInputTrgt() && (
                                    <Group.Control
                                        {...form.coCdInfo.schema.coCd}
                                        maxLength={CO_CD_INPUT_PROPERTIES[coTpCd].maxLength}
                                        placeholder={CO_CD_INPUT_PROPERTIES[coTpCd].placeholder}
                                    ></Group.Control>
                                )}
                                {!isLcmWrhsSelectTrgt() && !isCoCdInputTrgt() && (
                                    <Group.Control {...form.coCdInfo.schema.coCdRo}></Group.Control>
                                )}
                                {isLcmWrhsSelectTrgt() && (
                                    <Group.Control
                                        {...form.coCdInfo.schema.coCdSelect}
                                        options={getLcmWrhsOptionArr()}
                                        onChange={() => onLcmWrhsCoCdSlctChange()}
                                    />
                                )}
                                <Group.Control
                                    {...form.coCdInfo.schema.coTin}
                                    onChange={(data: any) => {
                                        fetch.getEpzLcnePrmssInfo.fetch(data);
                                    }}
                                    callback={(data: any) => {
                                        //console.log(data.data.coNm);
                                        form.coCdInfo.setValue("coTpNm", data?.data?.coNm);
                                    }}
                                ></Group.Control>
                            </Group.Row>
                            {/* Company Type Name 추가 */}
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.coTpNm}></Group.Control>
                                {!isLcmWrhsSelectTrgt() &&
                                    isCoCdInputTrgt() &&
                                    isCoCdInputTasacTrgt() &&
                                    !isSaveCocd() && (
                                        <Group.Control
                                            {...form.coCdInfo.schema.tasacCrtfNo}
                                            edit={comnUtils.isEmpty(coSn)}
                                            placeholder="TASAC License Number"
                                        ></Group.Control>
                                    )}
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.gainCfrmOmsnYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.gaotCfrmOmsnYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.stckCretCtraTpCd}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.stckClarCtraTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.autoCriYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.autoCroYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.wrhsDpstDays}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.psnWrhsYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.spcdlWrhsYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.rskCagYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.useYn}></Group.Control>
                                {isLcneUseTpCdSelectTrgt() && (
                                    <Group.Control {...form.coCdInfo.schema.lcneUseTpCd}></Group.Control>
                                )}
                            </Group.Row>
                            {isAtchFileVisible("bsopId") && (
                                <Group.Row>
                                    <Group.Control {...form.coCdInfo.schema.bsopId} controlSize={10} />
                                </Group.Row>
                            )}
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0407001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.onSaveBtnClick();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
